<script lang="jsx">
import { h } from "vue";
import { useStore } from "vuex";
import { closeSidebar, sidebarHide } from "../layouts";
import {
	isMenuFavorite,
	loadMenuFavorites,
	menuFavorites,
	toggleMenuFavorite,
} from "@/helpers/menuFavorites";

export default {
	props: {
		items: {
			type: Array,
			default: () => [],
		},
	},

	setup() {
		const { state } = useStore();
		loadMenuFavorites(state?.user?.id);
		return { storeState: state };
	},

	watch: {
		path: {
			immediate: true,
			handler() {
				this.syncActiveFromRoute();
			},
		},
		items: {
			deep: false,
			handler() {
				this.syncActiveFromRoute();
			},
		},
		"storeState.user.id": {
			immediate: true,
			handler(id) {
				loadMenuFavorites(id);
			},
		},
	},

	computed: {
		path() {
			return this.$route.path;
		},
		favorites() {
			return menuFavorites.value;
		},
		rootItems() {
			// props.items = [{ header, items: [...] }]
			const roots = [];
			for (const block of this.items || []) {
				if (Array.isArray(block?.items)) roots.push(...block.items);
			}
			return roots;
		},
	},

	methods: {
		/** Сбросить active у всего дерева (включая папки). */
		clearActive(items) {
			if (!Array.isArray(items)) return;
			for (const el of items) {
				el.active = false;
				if (el.items) this.clearActive(el.items);
			}
		},

		/** Открыть только цепочку до текущего route. */
		setActive(items, path) {
			if (!Array.isArray(items) || !path) return false;
			let found = false;
			for (const item of items) {
				item.active = false;
				if (item.route && this.routeMatches(item.route, path)) {
					item.active = true;
					found = true;
					continue;
				}
				if (item.items?.length) {
					if (this.setActive(item.items, path)) {
						item.active = true;
						found = true;
					}
				}
			}
			return found;
		},

		routeMatches(route, path) {
			if (!route || !path) return false;
			const r = String(route).split("?")[0];
			return r === path;
		},

		/** Цепочка [root, …, item] или null */
		findPath(items, target, trail = []) {
			if (!Array.isArray(items)) return null;
			for (const node of items) {
				const next = [...trail, node];
				if (node === target) return next;
				if (node.items?.length) {
					const found = this.findPath(node.items, target, next);
					if (found) return found;
				}
			}
			return null;
		},

		/** Закрыть соседей (и их потомков), кроме except */
		closeSiblings(siblings, except) {
			if (!Array.isArray(siblings)) return;
			for (const s of siblings) {
				if (s === except) continue;
				s.active = false;
				if (s.items?.length) this.clearActive(s.items);
			}
		},

		syncActiveFromRoute() {
			const run = () => {
				this.clearActive(this.rootItems);
				this.setActive(this.rootItems, this.path);
				this.$forceUpdate();
			};
			if ((this.items || []).length) run();
			else setTimeout(run, 50);
		},

		onClick(e, item) {
			e.preventDefault();
			const hasChildren = item.items && item.items.length > 0;

			if (hasChildren && sidebarHide.value) {
				sidebarHide.value = false;
			}

			if (hasChildren) {
				const wasOpen = !!item.active;
				const chain = this.findPath(this.rootItems, item);
				if (!chain?.length) return;

				// Аккордеон только между корневыми разделами
				const top = chain[0];
				for (const root of this.rootItems) {
					if (root !== top) {
						root.active = false;
						if (root.items?.length) this.clearActive(root.items);
					}
				}

				// Соседей на том же уровне закрыть
				const parent = chain.length >= 2 ? chain[chain.length - 2] : null;
				const siblings = parent?.items || this.rootItems;
				this.closeSiblings(siblings, item);

				// Предков оставить открытыми
				for (const node of chain) node.active = true;

				if (wasOpen) {
					item.active = false;
					this.clearActive(item.items);
				} else {
					item.active = true;
				}
				this.$forceUpdate();
				return;
			}

			// Лист: открыть цепочку до пункта и перейти
			this.clearActive(this.rootItems);
			if (item.route) this.setActive(this.rootItems, item.route);
			else item.active = true;

			if (item.route && this.$route.path !== item.route.split("?")[0]) {
				this.$router.push(item.route);
				closeSidebar();
			} else if (item.route) {
				closeSidebar();
			}
			this.$forceUpdate();
		},

		onToggleFavorite(e, item) {
			e.preventDefault();
			e.stopPropagation();
			if (!item?.route) return;
			toggleMenuFavorite(item, this.storeState?.user?.id);
			this.$forceUpdate();
		},

		pinButton(item) {
			if (!item?.route || (item.items && item.items.length > 0)) {
				return null;
			}
			const pinned = isMenuFavorite(item.route);
			return h(
				"button",
				{
					type: "button",
					class: {
						"sidebar-fav-btn": true,
						"is-pinned": pinned,
					},
					title: pinned
						? "Убрать из быстрого доступа"
						: "В быстрый доступ",
					onClick: (e) => this.onToggleFavorite(e, item),
				},
				[
					h("i", {
						class: pinned ? "icon icon-check" : "icon icon-plus",
					}),
				],
			);
		},

		link(item, { favoritable = true } = {}) {
			const { header, title, icon, route, active = false } = item;
			const pin =
				favoritable && item?.route && !(item.items && item.items.length)
					? this.pinButton(item)
					: null;
			const children = [h("i", { class: icon }), h("span", header)];
			if (pin) children.push(pin);

			return h(
				"a",
				{
					class: {
						"nav-link": true,
						active,
						"has-fav": !!pin,
					},
					title: title ? title : header,
					href: route ? route : "#",
					onClick: (e) => this.onClick(e, item),
				},
				children,
			);
		},

		menuItems(items) {
			return (items || []).map((item) => {
				const { active = false, items: children = [] } = item;
				const elements = [this.link(item)];

				if (children.length > 0) {
					elements.push(
						h(
							"ul",
							{
								class: "nav nav-group-sub",
								style: active ? "display: block;" : "",
							},
							this.menuItems(children),
						),
					);
				}

				return h(
					"li",
					{
						class:
							children.length > 0
								? active
									? "nav-item nav-item-submenu nav-item-open"
									: "nav-item nav-item-submenu"
								: "nav-item",
					},
					elements,
				);
			});
		},

		favoritesBlock() {
			const favs = this.favorites || [];
			const nodes = [
				h("li", { class: "nav-item-header sidebar-fav-header" }, [
					h(
						"div",
						{ class: "text-uppercase font-size-xs line-height-xs" },
						"Быстрый доступ",
					),
					h("i", {
						class: "icon icon-menu",
						title: "Закрепите пункты кнопкой «+»",
					}),
				]),
			];

			if (!favs.length) {
				nodes.push(
					h("li", { class: "nav-item sidebar-fav-empty" }, [
						h(
							"div",
							{ class: "sidebar-fav-empty__text" },
							"«+» у пункта — закрепить здесь",
						),
					]),
				);
				return nodes;
			}

			for (const fav of favs) {
				const active = this.routeMatches(fav.route, this.path);
				nodes.push(
					h(
						"li",
						{ class: { "nav-item": true, "sidebar-fav-item": true } },
						[
							this.link(
								{ ...fav, active, items: [] },
								{ favoritable: true },
							),
						],
					),
				);
			}
			return nodes;
		},
	},

	render() {
		const menus = [...this.favoritesBlock()];

		for (const item of this.items) {
			if (item.header) {
				menus.push(
					h("li", { class: "nav-item-header" }, [
						h(
							"div",
							{ class: "text-uppercase font-size-xs line-height-xs" },
							item.header,
						),
						h("i", {
							class: "icon icon-more-horizontal",
							title: "Main",
						}),
					]),
				);
			}
			// важно: развернуть массив, иначе Vue рисует «дыру» в меню
			menus.push(...this.menuItems(item.items || []));
		}

		return h("ul", { class: "nav nav-sidebar nav-sidebar--compact" }, menus);
	},
};
</script>

<style scoped>
/* компактные стили — в assets/scss/layout/sidebar.scss */
</style>
